import { bindable, customElement } from 'aurelia';
import template from './chronology-order-modal.html';
import { getMediaPreviewUrl } from '../services/api.service';
import type { MediaItem } from '../services/api.types';

@customElement({ name: 'chronology-order-modal', template })
export class ChronologyOrderModal {
  @bindable public items: MediaItem[] = [];
  @bindable public busy = false;
  @bindable public confirmed!: (items: MediaItem[]) => void;
  @bindable public closed!: () => void;

  public orderedItems: MediaItem[] = [];
  public selectedIds: Record<string, boolean> = {};
  public dragOverId: string | null = null;
  private anchorIndex = -1;
  private draggedIds: string[] = [];

  public get selectedCount(): number {
    return Object.values(this.selectedIds).filter(Boolean).length;
  }

  public binding(): void {
    this.resetItems(this.items);
  }

  public itemsChanged(items: MediaItem[]): void {
    this.resetItems(items);
  }

  private resetItems(items: MediaItem[]): void {
    this.orderedItems = [...items];
    this.selectedIds = {};
    this.anchorIndex = -1;
    this.dragOverId = null;
    this.draggedIds = [];
  }

  public previewUrl(item: MediaItem): string {
    return getMediaPreviewUrl(item.id, item.lastUpdatedAt || item.uploadedAt);
  }

  public isSelected(item: MediaItem): boolean {
    return !!this.selectedIds[item.id];
  }

  public selectItem(item: MediaItem, index: number, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.shiftKey && this.anchorIndex >= 0) {
      const start = Math.min(this.anchorIndex, index);
      const end = Math.max(this.anchorIndex, index);
      const next: Record<string, boolean> = {};
      for (let i = start; i <= end; i++) next[this.orderedItems[i].id] = true;
      this.selectedIds = next;
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      const next = { ...this.selectedIds };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = true;
      this.selectedIds = next;
      this.anchorIndex = index;
      return;
    }

    this.selectedIds = { [item.id]: true };
    this.anchorIndex = index;
  }

  public onDragStart(item: MediaItem, event: DragEvent): void {
    if (this.busy) {
      event.preventDefault();
      return;
    }

    if (!this.selectedIds[item.id]) {
      this.selectedIds = { [item.id]: true };
    }

    this.draggedIds = this.orderedItems
      .filter(candidate => this.selectedIds[candidate.id])
      .map(candidate => candidate.id);
    event.dataTransfer?.setData('text/plain', this.draggedIds.join(','));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  public onDragOver(item: MediaItem, event: DragEvent): void {
    if (this.busy || this.draggedIds.length === 0 || this.draggedIds.includes(item.id)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    this.dragOverId = item.id;
  }

  public onDragLeave(item: MediaItem): void {
    if (this.dragOverId === item.id) this.dragOverId = null;
  }

  public onDrop(item: MediaItem, event: DragEvent): void {
    event.preventDefault();
    if (this.busy || this.draggedIds.length === 0 || this.draggedIds.includes(item.id)) return;

    const moving = this.orderedItems.filter(candidate => this.draggedIds.includes(candidate.id));
    const remaining = this.orderedItems.filter(candidate => !this.draggedIds.includes(candidate.id));
    const targetIndex = remaining.findIndex(candidate => candidate.id === item.id);
    if (targetIndex < 0) return;

    this.orderedItems = [
      ...remaining.slice(0, targetIndex),
      ...moving,
      ...remaining.slice(targetIndex),
    ];
    this.dragOverId = null;
    this.draggedIds = [];
    this.anchorIndex = this.orderedItems.findIndex(candidate => candidate.id === moving[0]?.id);
  }

  public onDragEnd(): void {
    this.dragOverId = null;
    this.draggedIds = [];
  }

  public confirm(): void {
    if (this.busy || this.orderedItems.length < 2) return;
    this.confirmed([...this.orderedItems]);
  }

  public close(): void {
    if (!this.busy) this.closed();
  }
}
